import { zodResolver } from '@hookform/resolvers/zod'
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { ArrowLeft } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router'
import { z } from 'zod'

import { isApproverOrHigherRole } from '@ankor-io/common/domain/iam/account'
import { fromCents, toCents } from '@ankor-io/common/domain/pay/currency'
import { CharterStatus, type CharterSummary, type UpdateCharterRequest } from '@ankor-io/common/types/charter'
import { currencies } from '@ankor-io/common/util/currencies'

import { useCharter } from '@/hooks/useCharter'
import { useUserContext } from '@/providers/user/useUserContext'
import { Button } from '@/ui-components/Button'
import { ErrorMessage, Field, Label } from '@/ui-components/Fieldset'
import { Heading } from '@/ui-components/Heading'
import { Input } from '@/ui-components/Input'
import Loading from '@/ui-components/Loading'
import { Select } from '@/ui-components/Select'
import ConfirmationModal from '@/views/components/ConfirmationModal'

type CharterEditPageProps = Record<string, never>

// Charter fee, apa budget, vat rate are strings because the form schema does not coerce the values to numbers
// otherwise if we explicitly set the type to number in the form register itself, it won't form it's own non-user friendly error message
const formSchema = z.object({
  charterName: z.string().min(2, {
    message: 'Charter name is required.',
  }),
  charterFee: z
    .string()
    .min(1, {
      message: 'Charter fee is required.',
    })
    .refine(
      (val) => {
        const num = parseFloat(val)
        return !isNaN(num) && num >= 0 && num <= 9999999.99
      },
      {
        message: 'Amount must be between 0 and 9,999,999.99',
      },
    ),
  apaBudget: z
    .string()
    .min(1, {
      message: 'APA budget is required.',
    })
    .refine(
      (val) => {
        const num = parseFloat(val)
        return !isNaN(num) && num >= 0 && num <= 9999999.99
      },
      {
        message: 'Amount must be between 0 and 9,999,999.99',
      },
    ),
  primaryCurrency: z.string().min(1, {
    message: 'Currency is required.',
  }),
  vatRate: z.preprocess(
    (val) => (val === '' || val === null || typeof val === 'undefined' ? undefined : Number(val)),
    z
      .number()
      .min(1, { message: 'Tax amount must be greater than 0' })
      .max(100, { message: 'Tax amount cannot be greater than 100' }),
  ),
  embarkationDate: z.string().min(1, {
    message: 'Embarkation date is required.',
  }),
  disembarkationDate: z.string().min(1, {
    message: 'Disembarkation date is required.',
  }),
  placeOfDelivery: z.string().min(2, {
    message: 'Place of delivery is required.',
  }),
  placeOfRedelivery: z.string().min(2, {
    message: 'Place of redelivery is required.',
  }),
  chartererName: z.string().min(2, {
    message: 'Charterer name is required.',
  }),
  chartererEmail: z.string().email({
    message: 'Invalid email address.',
  }),
  countryCode: z
    .string()
    .min(2, {
      message: 'Country code is too short.',
    })
    .max(4, {
      message: 'Country code is too long',
    }),
  phoneNumber: z
    .string()
    .min(8, { message: 'Phone number is too short' })
    .max(14, { message: 'Phone number is too long' }),
})

const CharterEditPage: React.FC<CharterEditPageProps> = () => {
  const navigate = useNavigate()
  const { charterId } = useParams()
  const { getAccessToken } = useKindeAuth()
  const { charter, setCharter } = useCharter()
  const { activeOrganizationId, activeConnectedAccountId, activeOrganizationRole } = useUserContext()

  const [isLoading, setIsLoading] = useState(true)
  const [isUpdating, setIsUpdating] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([])
  const [isCancellingCharter, setIsCancellingCharter] = useState(false)
  const [isCancelCharterConfirmationModalOpen, setIsCancelCharterConfirmationModalOpen] = useState(false)

  const [hasTransactions, setHasTransactions] = useState(false)
  const [isCheckingTransactions, setIsCheckingTransactions] = useState(true)

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      charterName: '',
      charterFee: '',
      apaBudget: '',
      primaryCurrency: 'USD',
      vatRate: undefined,
      embarkationDate: '',
      disembarkationDate: '',
      placeOfDelivery: '',
      placeOfRedelivery: '',
      chartererName: '',
      chartererEmail: '',
      countryCode: '+1',
      phoneNumber: '',
    },
  })

  useEffect(() => {
    const getCharter = async () => {
      if (!charterId || !activeOrganizationId || !activeConnectedAccountId) {
        return
      }

      if (charterId === charter?.id) {
        setIsLoading(false)
        return
      }

      try {
        const token = await getAccessToken()
        const res = await fetch(
          `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/charter/${charterId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        )

        if (!res.ok) {
          throw new Error('Failed to fetch charter')
        }

        const charterData: CharterSummary = await res.json()
        setCharter(charterData)
      } catch (error) {
        console.error('Error fetching charter:', error)
        toast.error('Failed to fetch charter details')
      } finally {
        setIsLoading(false)
      }
    }

    getCharter()
  }, [charterId, activeOrganizationId, activeConnectedAccountId, getAccessToken, charter, setCharter])

  useEffect(() => {
    if (!charter) {
      return
    }

    form.reset({
      charterName: charter.name,
      charterFee: fromCents(charter.charterFeeCents).toString(),
      apaBudget: fromCents(charter.apaCents).toString(),
      primaryCurrency: charter.currencyCode,
      vatRate: Number(charter.vat),
      embarkationDate: charter.startDate.split('T')[0],
      disembarkationDate: charter.endDate.split('T')[0],
      placeOfDelivery: charter.delivery,
      placeOfRedelivery: charter.reDelivery,
      chartererName: charter.contactFullName,
      chartererEmail: charter.contactEmail,
      countryCode: charter.contactMobileCountryCode,
      phoneNumber: charter.contactMobileNumber,
    })
  }, [charter, form])

  useEffect(() => {
    const checkHasTransactions = async () => {
      if (!charterId || !activeOrganizationId || !activeConnectedAccountId) {
        setIsCheckingTransactions(false)
        return
      }

      setIsCheckingTransactions(true)
      try {
        const res = await fetch(
          `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/charter/${charterId}/transactions?take=1&skip=0`,
          {
            headers: {
              Authorization: `Bearer ${await getAccessToken()}`,
            },
          },
        )

        if (!res.ok) {
          throw new Error('Failed to fetch transactions')
        }

        const data = await res.json()
        setHasTransactions(Array.isArray(data) && data.length > 0)
      } catch (error) {
        console.error('Error checking charter transactions:', error)
        setHasTransactions(false)
      } finally {
        setIsCheckingTransactions(false)
      }
    }

    checkHasTransactions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [charterId, activeOrganizationId, activeConnectedAccountId])

  const uploadFiles = async (/* charterId: string */) => {
    if (uploadedFiles.length === 0) {
      return
    }

    setIsUploading(true)
    try {
      // TODO: Implement file upload and save to database
      toast.success(`${uploadedFiles.length} file(s) uploaded successfully.`)
    } catch (error) {
      console.error('File upload error:', error)
      toast.error('Some files failed to upload.')
    } finally {
      setIsUploading(false)
    }
  }

  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    if (!charter) return

    const submit = async () => {
      setIsUpdating(true)

      try {
        // Upload files if any
        if (uploadedFiles.length > 0) {
          await uploadFiles(/* charterId */)
        }

        // convert embarkationDate and disembarkationDate to ISO-8601 DateTime
        const payload: UpdateCharterRequest = {
          name: values.charterName,
          startDate: new Date(values.embarkationDate).toISOString(),
          endDate: new Date(values.disembarkationDate).toISOString(),
          delivery: values.placeOfDelivery,
          reDelivery: values.placeOfRedelivery,
          charterFeeCents: toCents(Number(values.charterFee)),
          apaCents: toCents(Number(values.apaBudget)),
          vat: values.vatRate.toString(),
          contactFullName: values.chartererName,
          contactEmail: values.chartererEmail,
          contactMobileNumber: values.phoneNumber,
          contactMobileCountryCode: values.countryCode,
          status: charter.status,
          ...(values.primaryCurrency !== charter.currencyCode && { currencyCode: values.primaryCurrency }),
        }

        const res = await fetch(
          `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/charter/${charter.id}`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${await getAccessToken()}`,
            },
            method: 'PUT',
            body: JSON.stringify(payload),
          },
        )

        if (!res.ok) {
          throw new Error('Failed to update charter.')
        }

        const updatedCharter: CharterSummary = await res.json()
        setCharter(updatedCharter)

        form.reset()
        setUploadedFiles([])

        // Navigate back to the charter detail page
        navigate(`/charters/${charter.id}`)
      } catch (error) {
        console.error('Error updating charter:', error)
        throw new Error('Failed to update charter.')
      } finally {
        setIsUpdating(false)
      }
    }

    toast.promise(submit, {
      loading: 'Updating charter...',
      success: 'Charter updated successfully.',
      error: 'Failed to update charter.',
    })
  }

  const handleCancelCharter = () => {
    const cancelCharter = async () => {
      if (!charter) {
        throw new Error('Charter not found')
      }

      // Currently with the dropdown field it closes upon clicking a button inside of it, so we won't really see this disabled but if the users internet is slow and go back into the menu, this can prevent it
      setIsCancellingCharter(true)

      try {
        // Convert embarkationDate and disembarkationDate to ISO-8601 DateTime
        const payload: UpdateCharterRequest = {
          ...charter,
          status: CharterStatus.CANCELLED,
        }
        delete payload.currencyCode

        const res = await fetch(
          `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/charter/${charter.id}`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${await getAccessToken()}`,
            },
            method: 'PUT',
            body: JSON.stringify(payload),
          },
        )

        if (!res.ok) {
          throw new Error('Failed to cancel charter.')
        }

        await res.json()

        form.reset()
        setUploadedFiles([])

        // Navigate back to the charter detail page
        navigate(`/charters/${charter.id}`)
      } catch (error) {
        console.error('Error cancelling charter:', error)
        throw new Error('Failed to cancel charter.')
      } finally {
        setIsCancelCharterConfirmationModalOpen(false)
        setIsCancellingCharter(false)
      }
    }

    toast.promise(cancelCharter, {
      loading: 'Cancelling charter...',
      success: 'Charter cancelled successfully.',
      error: 'Failed to cancel charter.',
    })
  }

  if (isLoading) {
    return <Loading />
  }

  if (!charter) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-8rem)] gap-2">
        <Heading level={3}>Charter not found</Heading>
        <Button
          color="primary"
          onClick={() => navigate(-1)}
        >
          Go back
        </Button>
      </div>
    )
  }

  return (
    <>
      <Button
        plain
        className="mb-2"
        aria-label="Go back"
        to={`/charters/${charter.id}`}
      >
        <ArrowLeft data-slot="icon" />
        Back
      </Button>

      <div className="space-y-6">
        <div className="mb-6">
          <Heading level={3}>Update Charter</Heading>
          <p className="text-gray-600 dark:text-gray-400">Update the charter details and documentation.</p>
        </div>

        <form
          role="form"
          onSubmit={form.handleSubmit(handleSubmit)}
          className="space-y-6"
        >
          {/* Charter Information Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-primary-900 dark:text-primary-100">Charter Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border rounded-lg p-4 border-gray-50 dark:border-gray-800">
              <Field>
                <Label>Charter Name</Label>
                <Input
                  placeholder="Charter Name"
                  {...form.register('charterName')}
                />
                {form.formState.errors.charterName && (
                  <ErrorMessage>{form.formState.errors.charterName.message}</ErrorMessage>
                )}
              </Field>

              <Field>
                <Label>Charter Fee</Label>
                <Input
                  type="number"
                  placeholder="Charter Fee"
                  {...form.register('charterFee')}
                />
                {form.formState.errors.charterFee && (
                  <ErrorMessage>{form.formState.errors.charterFee.message}</ErrorMessage>
                )}
              </Field>

              <Field>
                <Label>Contracted APA Amount</Label>
                <Input
                  type="number"
                  placeholder="APA Budget"
                  {...form.register('apaBudget')}
                />
                {form.formState.errors.apaBudget && (
                  <ErrorMessage>{form.formState.errors.apaBudget.message}</ErrorMessage>
                )}
              </Field>

              <Field>
                <Label>Primary Charter Currency</Label>
                <Select
                  disabled={isCheckingTransactions || hasTransactions}
                  {...form.register('primaryCurrency')}
                >
                  {currencies.map((currency) => (
                    <option
                      key={currency.code}
                      value={currency.code}
                    >
                      {currency.code}
                    </option>
                  ))}
                </Select>
                {hasTransactions && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Currency cannot be changed when this charter has transactions.
                  </p>
                )}
                {form.formState.errors.primaryCurrency && (
                  <ErrorMessage>{form.formState.errors.primaryCurrency.message}</ErrorMessage>
                )}
              </Field>

              <Field>
                <Label>VAT Rate (%)</Label>
                <Input
                  type="number"
                  placeholder="VAT Rate"
                  {...form.register('vatRate')}
                />
                {form.formState.errors.vatRate && <ErrorMessage>{form.formState.errors.vatRate.message}</ErrorMessage>}
              </Field>
            </div>
          </div>

          {/* Charter Dates & Locations Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-primary-900 dark:text-primary-100">Charter Dates & Locations</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border rounded-lg p-4 border-gray-50 dark:border-gray-800">
              <Field>
                <Label>Embarkation Date</Label>
                <Input
                  type="date"
                  {...form.register('embarkationDate')}
                />
                {form.formState.errors.embarkationDate && (
                  <ErrorMessage>{form.formState.errors.embarkationDate.message}</ErrorMessage>
                )}
              </Field>

              <Field>
                <Label>Disembarkation Date</Label>
                <Input
                  type="date"
                  {...form.register('disembarkationDate')}
                />
                {form.formState.errors.disembarkationDate && (
                  <ErrorMessage>{form.formState.errors.disembarkationDate.message}</ErrorMessage>
                )}
              </Field>

              <Field>
                <Label>Place of Delivery</Label>
                <Input
                  placeholder="Place of Delivery"
                  {...form.register('placeOfDelivery')}
                />
                {form.formState.errors.placeOfDelivery && (
                  <ErrorMessage>{form.formState.errors.placeOfDelivery.message}</ErrorMessage>
                )}
              </Field>

              <Field>
                <Label>Place of Re-delivery</Label>
                <Input
                  placeholder="Place of Re-delivery"
                  {...form.register('placeOfRedelivery')}
                />
                {form.formState.errors.placeOfRedelivery && (
                  <ErrorMessage>{form.formState.errors.placeOfRedelivery.message}</ErrorMessage>
                )}
              </Field>
            </div>
          </div>

          {/* Primary Charterer Details Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-primary-900 dark:text-primary-100">Primary Charterer Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border rounded-lg p-4 border-gray-50 dark:border-gray-800">
              <Field>
                <Label>Full Name</Label>
                <Input
                  placeholder="Full Name"
                  {...form.register('chartererName')}
                />
                {form.formState.errors.chartererName && (
                  <ErrorMessage>{form.formState.errors.chartererName.message}</ErrorMessage>
                )}
              </Field>

              <Field>
                <Label>Email</Label>
                <Input
                  type="email"
                  placeholder="Email"
                  {...form.register('chartererEmail')}
                />
                {form.formState.errors.chartererEmail && (
                  <ErrorMessage>{form.formState.errors.chartererEmail.message}</ErrorMessage>
                )}
              </Field>

              <Field>
                <Label>Country Code</Label>
                <Input
                  type="text"
                  placeholder="Country Code"
                  {...form.register('countryCode')}
                />
                {form.formState.errors.countryCode && (
                  <ErrorMessage>{form.formState.errors.countryCode.message}</ErrorMessage>
                )}
              </Field>

              <Field>
                <Label>Phone Number</Label>
                <Input
                  placeholder="Phone Number"
                  {...form.register('phoneNumber')}
                />
                {form.formState.errors.phoneNumber && (
                  <ErrorMessage>{form.formState.errors.phoneNumber.message}</ErrorMessage>
                )}
              </Field>
            </div>
          </div>

          <div className="flex justify-between space-x-2 pt-6">
            <Button
              color="red"
              className={`disabled:opacity-50 disabled:cursor-not-allowed ${
                !isApproverOrHigherRole(activeOrganizationRole) ||
                charter.status === CharterStatus.CANCELLED ||
                charter.status === CharterStatus.COMPLETED
                  ? 'invisible'
                  : ''
              }`}
              disabled={isCancellingCharter}
              onClick={() => setIsCancelCharterConfirmationModalOpen(true)}
            >
              Cancel Charter
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={isUploading || isUpdating || isCancellingCharter}
              className="disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Save Charter
            </Button>
          </div>
        </form>
      </div>

      <ConfirmationModal
        isOpen={isCancelCharterConfirmationModalOpen}
        onClose={() => setIsCancelCharterConfirmationModalOpen(false)}
        title="Cancel Charter"
        description="Are you sure you want to cancel this charter? You won't be able to attribute any expense to this charter anymore."
        confirmText="Cancel Charter"
        confirmLoadingText="Cancelling charter..."
        confirmColour="red"
        isLoading={isCancellingCharter}
        onConfirm={handleCancelCharter}
      />
    </>
  )
}

export default CharterEditPage
